import { STATES } from '../../../constants';
import { ITEM_KINDS, ITEM_STATUSES } from '@updater-core/constants/items';
import { BASE_PATH } from './constants';
import createMoveItemsInteractor from '@updater-core/interactors/move-items';

import { fetchIsExperimentFeatureEnabled } from '@updater-core/gateways/experiment';

/* Old insurance imports */
import { pick, merge } from 'lodash-es';
import { ITEM_DEFINITION_CODES } from '../../../constants';
import { selectors, actions } from '../../../state';
import { ENVIRONMENT } from '../../../environment';
import './insurance.global.css';
import { track, trackOn } from '@updater-core/lib/tracker';
import { getVariation } from '@updater-core/lib/optimizely';
import '../../tiled-header';

const PAGE_TITLE = 'Secure your Insurance';
const FIREHOSE_KEY = 'SYI_user_is_in_firehose';

const INSURANCE_ENV_NAME =
  ENVIRONMENT.name === 'test' ? 'development' : ENVIRONMENT.name;

export function InsuranceConfig($stateProvider) {
  'ngInject';

  $stateProvider.state(STATES.STEP_INSURANCE, {
    url: `${BASE_PATH}/*path`,
    resolve: {
      guard: () => {
        // Make sure to never resolve so that back works on the next page.
        return new Promise((resolve) => {
          return fetchIsExperimentFeatureEnabled({
            feature: 'syi_select_quote',
          }).then((enabled) => {
            if (enabled) {
              return resolve(); // No redirecting required.
            }
            const interactor = createMoveItemsInteractor();
            interactor.actions.fetchRemoteItems().then(() => {
              console.log({
                ITEM_KINDS,
                ITEM_STATUSES,
              });
              const metadata = interactor.selectors.itemMetadata(
                interactor.getState(),
                ITEM_KINDS.INSURANCE
              );
              const hasCheckedOutWithSure = metadata?.raw_state;
              if (hasCheckedOutWithSure) {
                window.location.href = '/n/dashboard/insurance/confirmation';
              } else {
                window.location.href = '/n/dashboard/insurance';
              }
            });
          });
        });
      },
    },
    // OLD INSURANCE CODE
    params: {
      toast: null,
      firehose: false,
    },
    views: {
      '@': {
        // TODO: `config` doesn't support all of our environments (e.g. demo).
        templateProvider: () =>
          // TODO: If/when the insurance app bundle splits itself and provides
          // a small entry point, we could remove bundle splitting here.
          // prettier-ignore
          import(
            /* webpackChunkName: "insurance" */
            '@updater/ui-move-your-insurance-step'
          )
          .then(() => `
            <v1-move-your-insurance-step
              base="${BASE_PATH}"
              config="${INSURANCE_ENV_NAME}"
              class="container micro-app-fixes"
            >
            </v1-move-your-insurance-step>
          `),
        controller,
        controllerAs: 'ctrl',
      },
    },
    disableBreadcrumb: true,
    scrollTop: true,
    title: PAGE_TITLE,
    titleBar: {
      title: PAGE_TITLE,
      icon: 'security-no-lock',
    },
    analytics: {
      ignore: true,
    },
    data: {
      permissions: 'signedIn && moveConfirmed && hasOwnership',
      confirmState: STATES.STEP_INSURANCE_CONFIRM_MOVE,
      moveItem: {
        type: ITEM_KINDS.INSURANCE,
        singleton: true,
      },
    },
  });
  $stateProvider.state(STATES.STEP_INSURANCE_CONFIRM_MOVE, {
    url: '/insurance-confirm-move',
    views: {
      '@': {
        template: `<upd-confirm-move></upd-confirm-move>`,
      },
    },
    scrollTop: true,
    title: 'Insurance Confirm Move',
    analytics: {
      page: 'Insurance Confirm Move',
    },
    data: {
      permissions: 'signedIn && !(moveConfirmed && hasOwnership)',
      successState: STATES.STEP_INSURANCE,
    },
  });
}

const INSURANCE_ADDRESS_FIELDS = [
  'apt',
  'city',
  'id',
  'ownership',
  'state',
  'street',
  'zip',
];

function controller($scope, $element, updEnvironment, updStore, $stateParams) {
  'ngInject';

  if ($stateParams.firehose) {
    localStorage.setItem(FIREHOSE_KEY, 'true');
  }
  $scope.$on('$destroy', () => {
    localStorage.removeItem(FIREHOSE_KEY);
  });

  const insuranceStep = $element.find('v1-move-your-insurance-step')[0];
  const state = updStore.getState();
  const user = selectors.current.user(state);
  const move = selectors.current.move(state);
  const entities = selectors.current.entities(state);
  const itemDefinition = selectors.itemDefinition.byCode(
    ITEM_DEFINITION_CODES.INSURANCE
  );

  // TODO: Create selectors for these.
  const entity = entities.find(({ id }) => id === user.siteEntityId(state));
  const parentEntity = entities.find(({ id }) => id === entity.parent_id);
  const providerType =
    itemDefinition.page(state) !== null && itemDefinition.page(state).required
      ? 'required'
      : 'recommended';

  const generateEvent = (attributes) =>
    merge({}, attributes, {
      domain: 'Secure Your Insurance',
      provider: {
        type: providerType,
      },
    });

  const dispatchRoute = (route, params) => {
    updStore.dispatch(actions.router.stateGo(route, params));
  };

  const shouldSeeSurvey = (payload) => {
    updStore.dispatch(actions.insurance.shouldSeeSurvey(payload));
  };

  // Only track in the insurance step
  const trackOff = trackOn(insuranceStep, generateEvent);
  $scope.$on('$destroy', trackOff);

  const tracker = {
    track: (attributes) => {
      track(
        generateEvent({
          ...attributes,
          details: attributes.payload,
        })
      );
    },
  };

  // TODO: Micro task delay is necessary to allow `ui-router` to finish
  // transition URLs, else the insurance app's Vue Router engages too early and
  // attempts to route based on the wrong URL.
  Promise.resolve().then(() => {
    insuranceStep.context = {
      cdn_url: updEnvironment.endpoints.cloudinary,
      experiments: {
        getVariation: (experimentKey, attributes) =>
          getVariation(experimentKey, attributes),
      },
      dispatch: updStore.dispatch,
      skipStep: () =>
        updStore.dispatch(
          actions.step.goToNext(selectors.step.nextStep(updStore.getState()))
        ),
      tracker,
      shouldSeeSurvey: shouldSeeSurvey,
      dispatchRoute: dispatchRoute,
      user: {
        id: user.id(state),
        uuid: user.uuid(state),
        firstName: user.firstName(state),
        lastName: user.lastName(state),
        cellPhone: user.cellPhone(state),
        homePhone: user.homePhone(state),
        test: user.test(state),
      },
      move: {
        id: move.id(state),
        date: move.date(state),
        fromAddress: pick(move.fromAddress(state), INSURANCE_ADDRESS_FIELDS),
        toAddress: pick(move.toAddress(state), INSURANCE_ADDRESS_FIELDS),
      },
      entity: {
        id: entity.id,
        config: {
          ...entity.config,
          renters_insurance: itemDefinition.page(state),
          banner_html: itemDefinition.bannerHtml(state),
        },
        parent: parentEntity && {
          id: parentEntity.id,
          config: parentEntity.config,
        },
      },
      'bypass-auth': INSURANCE_ENV_NAME === 'development',
      ...selectors.session.headers(state),
    };
  });
}
// OLD INSURANCE CODE END
